import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { FieldName } from '../../../types/CardFieldName';
import { type CardNetworkMeta, getCardNetworkMetaByNetwork, getCardNetworkMetaByNumber } from './cardNetworksMeta';
import { transformExpireStrToObj } from './formatting';
import { sanitizeCardNumber } from './sanitizeUtils';

export const validateField = (
  fieldName: FieldName,
  data: {
    value: string;
    cardNetwork?: CreditCardNetwork;
  },
): boolean => {
  switch (fieldName) {
    case FieldName.Number:
      return validateCardNumber(data.value);
    case FieldName.CVV:
      return validateCardCVC(data.value, data.cardNetwork);
    case FieldName.ExpireDate: {
      const { month, year } = transformExpireStrToObj(data.value);
      return validateCardExpiry(month, year);
    }
    default:
      return !!data.value;
  }
};

export const validateCardExpiry = (month: number, year: number): boolean => {
  if (!(month && year)) {
    return false;
  }

  if (!(month >= 1 && month <= 12)) {
    return false;
  }

  if (String(year).length !== 4 && String(year).length !== 2) {
    return false;
  }

  let year4Digits = year;

  if (String(year).length === 2) {
    year4Digits = parseInt(`20${year}`, 10);
  }

  const expiry = new Date(year4Digits, month);
  const currentTime = new Date();

  return expiry > currentTime && year4Digits < 2100;
};

export const validateCardCVC = (cvc: string, network): boolean => {
  const clearedCvc = String(cvc).trim();
  const regExp = /^\d+$/;
  const isValid = regExp.test(clearedCvc);

  if (!isValid) {
    return false;
  }

  const card = getCardNetworkMetaByNetwork(network);

  if (card) {
    return card.cvcLengths.some((length) => clearedCvc.length === length);
  }

  return clearedCvc.length >= 3 && clearedCvc.length <= 4;
};

const validateNumberWithLuhn = (cardNumber: string): boolean => {
  let sum = 0;
  const parity = cardNumber.length % 2;
  let digit;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    digit = parseInt(cardNumber[i], 10);

    if (i % 2 === parity) {
      digit *= 2;
    }

    if (digit > 9) {
      digit -= 9;
    }

    sum += digit;
  }

  return sum % 10 === 0;
};

export const validateCardNumber = (cardNumber: string): boolean => {
  const sanitizedCardNumber: string = sanitizeCardNumber(cardNumber);
  const isValidCardNumber: boolean = /^\d+$/.test(sanitizedCardNumber);

  if (!isValidCardNumber) {
    return false;
  }

  const cardNetworkMeta: CardNetworkMeta = getCardNetworkMetaByNumber(sanitizedCardNumber);

  if (!cardNetworkMeta) {
    return false;
  }

  const isValidLength = cardNetworkMeta.lengths.includes(sanitizedCardNumber.length);
  const isLuhnCheckValid = validateNumberWithLuhn(sanitizedCardNumber);
  const { luhn } = cardNetworkMeta;

  return isValidLength && (!luhn || isLuhnCheckValid);
};

const checkIsCardNumberFinished = (cardNumber: string, cardNetwork: CreditCardNetwork): boolean => {
  const cardLength = cardNumber.length;
  if (!cardNetwork) {
    // NOTE: minimum card number length is 8 in our meta
    return cardLength >= 8;
  }
  const minimumPossibleNumberLength = getCardNetworkMetaByNetwork(cardNetwork).lengths[0];
  return cardLength >= minimumPossibleNumberLength;
};

const checkIsExpireDateFinished = (expireDate: string): boolean => {
  return expireDate.length === 7;
};

const checkIsCVVFinished = (cvv: string, cardNetwork: CreditCardNetwork): boolean => {
  if (!cardNetwork) {
    return cvv.length >= 3;
  }
  const cvvLengths = getCardNetworkMetaByNetwork(cardNetwork).cvcLengths;
  return cvvLengths.some((length) => cvv.length >= length);
};

export const isFieldFinished = ({
  fieldName,
  value,
  cardNetwork,
}: {
  fieldName: FieldName;
  value: string;
  cardNetwork: CreditCardNetwork;
}): boolean => {
  switch (fieldName) {
    case FieldName.Number:
      return checkIsCardNumberFinished(value, cardNetwork);
    case FieldName.ExpireDate:
      return checkIsExpireDateFinished(value);
    case FieldName.CVV:
      return checkIsCVVFinished(value, cardNetwork);
    case FieldName.HolderName: {
      return !!value;
    }
    default:
      return !!value;
  }
};
